import { UserAttributes } from "../attributes/user-attributes";
import { BusinessAttributes } from "../attributes/business-attributes";
import { PaginatorAttributes } from "../attributes/paginator-attributes";
import Business from "./Business";
import User from "./User";
import BusinessBranch from "./BusinessBranch";
import { BusinessBranchAttributes } from "../attributes/business-branch-attributes";
import BranchEmployee from "./BranchEmployee";
import { BranchEmployeeAttributes } from "../attributes/branch-employee-attributes";
import BranchManager from "./BranchManager";
import { BranchManagerAttributes } from "../attributes/branch-manager-attributes";
import { CountryAttributes } from "../attributes/country-attributes";
import Country from "./Country";
import { AnnouncementsAttributes } from "../attributes/announcements-attributes";
import Announcement from "./Announcement";
import { RaffleDrawParticipantAttributes } from "../attributes/raffle-draw-participant-attributes";
import RaffleDrawParticipant from "./RaffleDrawParticipant";
import { BusinessWithdrawTafificationAttributes } from "../attributes/business-withdraw-tafification-attributes";
import BusinessWithdrawTarification from "./BusinessWithdrawTafification";
import IcipWithdrawTafification from "./IcipWithdrawTarification";
import { IcipWithdrawTafificationAttributes } from "../attributes/icip-withdraw-tafification-attributes";
import Pricing from "./Pricing";
import { PricingAttributes } from "../attributes/pricing-attributes";
import ExchangeRate from "./ExchangeRate";
import { ExchangeRateAttributes } from "../attributes/exchange-rate-attributes";
import BusinessWithdrawPercent from "./BusinessWithdrawPercent";
import { BusinessWithdrawPercentAttributes } from "../attributes/business-withdraw-percent-attributes";
import Faq from "./Faq";
import { FaqAttributes } from "../attributes/faq-attributes";
import PricingGroup from "./PricingGroup";
import { PricingGroupAttributes } from "../attributes/pricing-group-attributes";

import AccountLimit from "./AccountLimit";
import { AccountLimitAttributes } from "../attributes/account-limit-attributes";
import BaseAccountConfiguration from "./BaseAccountConfiguration";
import { BaseAccountConfigurationAttributes } from "../attributes/base-account-conf-attributes";
import WithdrawRequest from "./WithdrawRequest";
import { WithdrawRequestAttributes } from "../attributes/withdraw-request-attributes";
import { ActivitySchemaAttributes } from "../attributes/activity-schema-attributes";
import ActivitySchema from "./ActivitySchema";

export enum DataTypeEnum {
  business = "business",
  users = "users",
  branches = "branches",
  employees = "employees",
  managers = "managers",
  team_business = "team_business",
  countries = "countries",
  announcements = "announcements",
  raffleParticipants = "raffleParticipants",
  withdrawTafification = "withdrawTafification",
  icipWithdrawTarification = "icipWithdrawTarification",
  pricing = "pricing",
  withdrawPercent = "withdrawPercent",
  exchangeRate = "exchangeRate",
  faq = "faq",
  pricingGroup = "pricingGroup",
  accountLimit = "accountLimit",
  baseAccountConf = "baseAccountConf",
  withdraw_requests = "withdraw_requests",
  activity_schema = "activity_schema",
}

export type DataType =
  | Business
  | User
  | BusinessBranch
  | BranchEmployee
  | BranchManager
  | Country
  | Announcement
  | RaffleDrawParticipant
  | BusinessWithdrawTarification
  | IcipWithdrawTafification
  | Pricing
  | BusinessWithdrawPercent
  | ExchangeRate
  | Faq
  | PricingGroup
  | AccountLimit
  | BaseAccountConfiguration
  | RaffleDrawParticipant
  | WithdrawRequest
  | ActivitySchema;

class Paginator {
  total: number;
  last_page: number;
  path: string;
  page: number;
  total_page: number;
  per_page: number;
  next_page: string;
  prev_page: string;
  items: Array<DataType>;

  constructor(
    total: number,
    last_page: number,
    path: string,
    page: number,
    per_page: number,
    prev_page: string,
    next_page: string,
    items: Array<DataType>
  ) {
    this.total = total;
    this.last_page = last_page;
    this.path = path;
    this.page = page;
    this.per_page = per_page;
    this.prev_page = prev_page;
    this.next_page = next_page;
    this.items = items;
    this.total_page = total === 0 ? 0 : Math.ceil(total / per_page);
  }

  static empty(): Paginator {
    return new Paginator(0, 0, "", 0, 0, null, null, []);
  }

  static fromJson(json: PaginatorAttributes, type: DataTypeEnum): Paginator {
    return new Paginator(
      json.total,
      json.last_page,
      json.path,
      json.page,
      json.per_page,
      json.prev_page,
      json.next_page,
      Paginator.buildItems(json.items, type)
    );
  }

  clone(): Paginator {
    return new Paginator(
      this.total,
      this.last_page,
      this.path,
      this.page,
      this.per_page,
      this.prev_page,
      this.next_page,
      this.items
    );
  }

  cloneItems(items: Array<DataType>): Paginator {
    return new Paginator(
      this.total,
      this.last_page,
      this.path,
      this.page,
      this.per_page,
      this.prev_page,
      this.next_page,
      items
    );
  }

  static buildItems(
    json: Array<
      | BusinessAttributes
      | UserAttributes
      | BusinessBranchAttributes
      | BranchEmployeeAttributes
      | BranchManagerAttributes
      | CountryAttributes
      | AnnouncementsAttributes
      | RaffleDrawParticipantAttributes
      | BusinessWithdrawTarification
      | IcipWithdrawTafification
      | PricingAttributes
      | BusinessWithdrawPercentAttributes
      | ExchangeRateAttributes
      | FaqAttributes
      | PricingGroupAttributes
      | AccountLimitAttributes
      | BaseAccountConfigurationAttributes
      | WithdrawRequestAttributes
      | ActivitySchemaAttributes
    >,
    type: DataTypeEnum
  ): Array<DataType> {
    let result: Array<DataType> = [];

    json.forEach((item, index) => {
      if (item !== null && item !== undefined) {
        switch (type) {
          case DataTypeEnum.team_business: // BranchEmployee | BranchManager
            if (Object.keys(item).indexOf("employee") > 0) {
              result.push(
                BranchEmployee.fromJson(item as BranchEmployeeAttributes)
              );
            }

            if (Object.keys(item).indexOf("manager") > 0) {
              result.push(
                BranchManager.fromJson(item as BranchManagerAttributes)
              );
            }
            break;
          case DataTypeEnum.business:
            result.push(Business.fromJson(item as BusinessAttributes));
            break;
          case DataTypeEnum.users:
            result.push(User.fromJson(item as UserAttributes));
            break;
          case DataTypeEnum.countries:
            result.push(Country.fromJson(item as CountryAttributes));
            break;
          case DataTypeEnum.branches:
            result.push(
              BusinessBranch.fromJson(item as BusinessBranchAttributes)
            );
            break;
          case DataTypeEnum.employees:
            result.push(
              BranchEmployee.fromJson(item as BranchEmployeeAttributes)
            );
            break;
          case DataTypeEnum.announcements:
            result.push(Announcement.fromJson(item as AnnouncementsAttributes));
            break;
          case DataTypeEnum.raffleParticipants:
            result.push(
              RaffleDrawParticipant.fromJson(
                item as RaffleDrawParticipantAttributes
              )
            );
            break;
          case DataTypeEnum.withdraw_requests:
            result.push(
              WithdrawRequest.fromJson(item as WithdrawRequestAttributes)
            );
            break;
          case DataTypeEnum.withdrawTafification:
            result.push(
              BusinessWithdrawTarification.fromJson(
                item as BusinessWithdrawTafificationAttributes
              )
            );
            break;
          case DataTypeEnum.icipWithdrawTarification:
            result.push(
              IcipWithdrawTafification.fromJson(
                item as IcipWithdrawTafificationAttributes
              )
            );
            break;
          case DataTypeEnum.pricing:
            result.push(Pricing.fromJson(item as PricingAttributes));
            break;
          case DataTypeEnum.withdrawPercent:
            result.push(
              BusinessWithdrawPercent.fromJson(
                item as BusinessWithdrawPercentAttributes
              )
            );
            break;
          case DataTypeEnum.exchangeRate:
            result.push(ExchangeRate.fromJson(item as ExchangeRateAttributes));
            break;
          case DataTypeEnum.faq:
            result.push(Faq.fromJson(item as FaqAttributes));
            break;

          case DataTypeEnum.pricingGroup:
            result.push(PricingGroup.fromJson(item as PricingGroupAttributes));
            break;
          case DataTypeEnum.accountLimit:
            result.push(AccountLimit.fromJson(item as AccountLimitAttributes));
            break;
          case DataTypeEnum.baseAccountConf:
            result.push(
              BaseAccountConfiguration.fromJson(
                item as BaseAccountConfigurationAttributes
              )
            );
            break;
          case DataTypeEnum.activity_schema:
            result.push(
              ActivitySchema.fromJson(item as ActivitySchemaAttributes)
            );
            break;
        }
      }
    });

    return result;
  }
}

export default Paginator;
